import React, { useCallback } from 'react';
import useStyles from 'hooks/useStyles';
import { string, object } from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Link from 'components/Link';
import SnapaskLoader from 'components/SnapaskLoader';
import SeeMoreRow from 'components/SeeMoreRow';
import LoadedImage from 'components/LoadedImage';

import {
  getInstructorList,
  getInstructorListPageSetting,
} from 'reduxModules/instructorProfile/selector';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { getFetchInstructorListState } from 'reduxModules/actionStatus/selector';
import { fetchInstructorList } from 'reduxModules/instructorProfile/action';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';
import useGetCurrentImageType from 'hooks/useGetCurrentImageType';

import { COURSE_ALL_INSTRUCTOR } from 'constants/courseEventSections';
import { RC_ALL_INSTRUCTOR } from 'constants/liveEventSections';

import {
  INSTRUCTOR_TEMPLATE,
  REGULAR_CLASS_TYPE,
  COURSE_TYPE,
} from 'constants/routingPath';
import {
  COURSE_INSTRUCTOR_CLICK,
  REGULAR_CLASS_INSTRUCTOR_CLICK,
} from 'constants/gtmEvent';
import styles from './Instructors.scss';

function Instructors({ contentType, instructorListRef }) {
  useStyles(styles);
  const dispatch = useDispatch();
  const instructorList = useSelector(getInstructorList);
  const { isLoading } = useSelector(getFetchInstructorListState, shallowEqual);
  const { page: currentPage, hasMorePage } = useSelector(
    getInstructorListPageSetting,
  );

  const { imageType, setContainerRef } = useGetCurrentImageType();

  const getOfficialSiteURL = useGetOfficialSiteURL();
  const { t } = useTranslation(['instructorProfile']);

  const eventNameMapping = {
    [REGULAR_CLASS_TYPE]: REGULAR_CLASS_INSTRUCTOR_CLICK,
    [COURSE_TYPE]: COURSE_INSTRUCTOR_CLICK,
  };

  const sectionNameMapping = {
    [REGULAR_CLASS_TYPE]: RC_ALL_INSTRUCTOR,
    [COURSE_TYPE]: COURSE_ALL_INSTRUCTOR,
  };

  const isListEmpty = instructorList.length === 0;

  const handleLinkClick = ({
    id,
    name,
    hashtagString,
    subjectString,
    askableTutorId,
  }) => {
    dispatch(
      sendGTMEvent({
        eventName: eventNameMapping[contentType],
        data: {
          instructorId: id,
          instructorName: name,
          instructorHashtag: hashtagString,
          subjects: subjectString,
          tutorService: askableTutorId !== null,
          section: sectionNameMapping[contentType],
        },
      }),
    );
  };

  const loadNextPage = useCallback(() => {
    const pageSetting = {
      page: currentPage + 1,
    };
    dispatch(fetchInstructorList({ type: contentType, pageSetting }));
  }, [dispatch, currentPage, contentType]);

  const renderInstructorList = instructor => {
    const {
      avatar,
      name,
      subjectString,
      id,
      liveTopicCount,
      courseCount,
    } = instructor;

    return (
      <li
        className={styles.instructorListItem}
        key={`${id}${name}}`}
        data-ooo-image-container
      >
        <Link
          to={getOfficialSiteURL(INSTRUCTOR_TEMPLATE, { id })}
          onClick={() => handleLinkClick(instructor)}
        >
          <LoadedImage
            src={avatar[imageType]}
            alt={name}
            className={styles.avatar}
          />
          <div className={styles.info}>
            <h3 className={styles.name}> {name} </h3>
            <p className={styles.subjects}> {subjectString} </p>
            <ul className={styles.lessonCount}>
              {liveTopicCount > 0 && (
                <li className={styles.lessonCountItem}>
                  <span className={styles.count}>{liveTopicCount}</span>
                  <span>
                    {t('instructorProfile.regularClass', {
                      count: liveTopicCount,
                    })}
                  </span>
                </li>
              )}
              {courseCount > 0 && (
                <li className={styles.lessonCountItem}>
                  <span className={styles.count}>{courseCount}</span>
                  <span>
                    {t('instructorProfile.course', {
                      count: courseCount,
                    })}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </Link>
      </li>
    );
  };

  return (
    <>
      <div className={styles.sentinel} ref={instructorListRef} />
      <div ref={setContainerRef} className={styles.root}>
        <h3 className={styles.title}> {t('instructorList.title')} </h3>

        {isLoading && isListEmpty && (
          <div className={styles.loader}>
            <SnapaskLoader />
          </div>
        )}

        {!isLoading && (
          <>
            <ul className={styles.instructorList}>
              {instructorList.map(renderInstructorList)}
            </ul>
            {hasMorePage && (
              <SeeMoreRow onClick={loadNextPage} className={styles.seeMore} />
            )}
          </>
        )}
      </div>
    </>
  );
}

Instructors.propTypes = {
  contentType: string.isRequired,
  instructorListRef: object.isRequired,
};

export default Instructors;
